export type Margins = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export type CenteredInnerDims = {
  innerWidth: number;
  innerHeight: number;
  centeredMargins: Margins;
};

export type SquareLayoutBase = {
  squareSide: number;
  offsetX: number;
  offsetY: number;
};

export type SquareLayoutWithCells = SquareLayoutBase & {
  cellW: number;
  cellH: number;
};

/**
 * Computes inner drawing dimensions with vertical centering when space allows.
 */
export function computeCenteredInnerDims(
  svgWidth: number,
  svgHeight: number,
  margin: Margins,
): CenteredInnerDims {
  const innerWidth = Math.max(0, svgWidth - margin.left - margin.right);
  const maxHeight = Math.max(0, svgHeight - margin.top - margin.bottom);
  const innerHeight = maxHeight / innerWidth > 1.35 ? innerWidth : maxHeight;
  const centerYOffset = Math.max(0, (maxHeight - innerHeight) / 2);
  const centeredMargins: Margins = {
    ...margin,
    top: margin.top + centerYOffset,
  };
  return { innerWidth, innerHeight, centeredMargins };
}

/**
 * Computes square layout offsets; optionally returns cell sizes when rows/cols are provided.
 */
export function computeSquareLayout(
  innerWidth: number,
  innerHeight: number,
  margins: Margins,
  padding: number,
  rows: number,
  cols: number,
): SquareLayoutWithCells;

export function computeSquareLayout(
  innerWidth: number,
  innerHeight: number,
  margins: Margins,
  padding?: number,
  rows?: number,
  cols?: number,
): SquareLayoutBase;

export function computeSquareLayout(
  innerWidth: number,
  innerHeight: number,
  margins: Margins,
  padding = 5,
  rows?: number,
  cols?: number,
): SquareLayoutBase | SquareLayoutWithCells {
  const squareSide = Math.max(
    padding,
    Math.min(innerWidth, innerHeight) - padding,
  );
  const offsetX = margins.left + (innerWidth - squareSide) / 2;
  const offsetY = margins.top + (innerHeight - squareSide) / 2;

  if (rows && cols) {
    const cellW = squareSide / cols;
    const cellH = squareSide / rows;
    return { squareSide, offsetX, offsetY, cellW, cellH };
  }

  return { squareSide, offsetX, offsetY };
}
